import React from 'react';

import Checkbox from '@amzn/meridian/checkbox';
import DataTable, {
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableHeader,
  DataTableRow,
} from '@amzn/meridian/data-table';
import Text from '@amzn/meridian/text';
import type { RegionTask, RegionTaskModification } from 'src/services/tasks/types';

// **** Types **** //

interface ModificationMatrixProps {
  modifications: RegionTaskModification[];
  tasks: RegionTask[];
  enabledByTask: Record<number, Set<number>>;
  rowCheckState: (modificationId: number) => boolean | 'mixed';
  onToggleCell: (taskId: number, modificationId: number, checked: boolean) => void;
  onToggleRow: (modificationId: number, checked: boolean) => void;
}

// **** Components **** //

/**
 * Presentational matrix of modification checkboxes for a task-group. Each row is
 * a modification from the catalog and each column a child-task, plus a leading
 * "select all" column that toggles a modification across every child-task. A
 * checked cell means that modification is assigned to that task. Single tasks
 * use `ModificationList` instead. State and chrome are owned by
 * `ModificationEditor`.
 */
const ModificationMatrix = ({
  modifications,
  tasks,
  enabledByTask,
  rowCheckState,
  onToggleCell,
  onToggleRow,
}: ModificationMatrixProps) => (
  <DataTable size="medium" showRowDividers stickyHeaderRows>
    <DataTableHead>
      <DataTableRow>
        {/* Leading "select all" column has no header. */}
        <DataTableHeader />
        <DataTableHeader textAlign="start">
          <Text type="h50">Modification</Text>
        </DataTableHeader>
        {tasks.map((task) => (
          <DataTableHeader key={task.taskId} textAlign="start">
            <Text id={`mm-task-${task.taskId}`} type="h50">
              {task.label}
            </Text>
          </DataTableHeader>
        ))}
      </DataTableRow>
    </DataTableHead>
    <DataTableBody>
      {modifications.map((mod) => (
        <DataTableRow key={mod.modificationId}>
          <DataTableCell containsSingleFocusableChild={true}>
            <Checkbox
              aria-label={`Toggle all tasks for ${mod.name}`}
              checked={rowCheckState(mod.modificationId)}
              onChange={(checked) => onToggleRow(mod.modificationId, checked)}
            />
          </DataTableCell>
          <DataTableCell>
            <Text id={`mm-mod-${mod.modificationId}`} type="b300">
              {mod.name}
            </Text>
          </DataTableCell>
          {tasks.map((task) => (
            <DataTableCell key={task.taskId} containsSingleFocusableChild={true}>
              <Checkbox
                aria-labelledby={`mm-mod-${mod.modificationId} mm-task-${task.taskId}`}
                checked={enabledByTask[task.taskId]?.has(mod.modificationId) ?? false}
                onChange={(checked) => onToggleCell(task.taskId, mod.modificationId, checked)}
              />
            </DataTableCell>
          ))}
        </DataTableRow>
      ))}
    </DataTableBody>
  </DataTable>
);

// **** Export **** //

export default ModificationMatrix;
